import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Box, View, RotateCcw, ZoomIn, ZoomOut, Loader2, Smartphone } from 'lucide-react';

export default function ModelViewer({ 
    src, 
    alt = "3D Model",
    poster = null,
    primaryColor = "#C9A227",
    backgroundColor = "#0D0D0D",
    className = ""
}) {
    const modelViewerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadProgress, setLoadProgress] = useState(0);
    const [arSupported, setArSupported] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Check AR support
        const checkARSupport = async () => {
            // Check WebXR support
            if ('xr' in navigator) {
                try {
                    const supported = await navigator.xr?.isSessionSupported('immersive-ar');
                    if (supported) {
                        setArSupported(true);
                        return;
                    }
                } catch (e) {
                    console.log('WebXR AR not supported');
                }
            }
            
            // Check iOS Quick Look support (Safari on iOS)
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            if (isIOS) {
                setArSupported(true);
                return;
            }
            
            // Check Android Scene Viewer support
            const isAndroid = /android/i.test(navigator.userAgent);
            if (isAndroid) {
                setArSupported(true);
                return;
            }
            
            setArSupported(false);
        };

        checkARSupport();
    }, []);

    useEffect(() => {
        const modelViewer = modelViewerRef.current;
        if (!modelViewer) return;

        const handleLoad = () => {
            setIsLoading(false);
            setLoadProgress(100);
        };

        const handleError = (event) => {
            setIsLoading(false);
            setError('Failed to load 3D model');
            console.error('Model viewer error:', event);
        };

        const handleProgress = (event) => {
            const progress = event.detail.totalProgress * 100;
            setLoadProgress(Math.round(progress));
        };

        modelViewer.addEventListener('load', handleLoad);
        modelViewer.addEventListener('error', handleError);
        modelViewer.addEventListener('progress', handleProgress);

        return () => {
            modelViewer.removeEventListener('load', handleLoad);
            modelViewer.removeEventListener('error', handleError);
            modelViewer.removeEventListener('progress', handleProgress);
        };
    }, [src]);

    const handleViewAR = () => {
        if (modelViewerRef.current) {
            modelViewerRef.current.activateAR();
        }
    };

    const handleResetCamera = () => {
        if (modelViewerRef.current) {
            modelViewerRef.current.cameraOrbit = "0deg 75deg 105%";
            modelViewerRef.current.fieldOfView = "auto";
        }
    };

    const handleZoomIn = () => {
        if (modelViewerRef.current) {
            const currentFov = modelViewerRef.current.getFieldOfView();
            modelViewerRef.current.fieldOfView = `${Math.max(10, currentFov - 10)}deg`;
        }
    };

    const handleZoomOut = () => {
        if (modelViewerRef.current) {
            const currentFov = modelViewerRef.current.getFieldOfView();
            modelViewerRef.current.fieldOfView = `${Math.min(90, currentFov + 10)}deg`;
        }
    };

    const isDark = backgroundColor.toLowerCase() === '#0d0d0d' || 
                   backgroundColor.toLowerCase() === '#1a1a1a' || 
                   backgroundColor.toLowerCase() === '#000000' ||
                   backgroundColor.toLowerCase() === '#000';

    if (!src) {
        return (
            <div 
                className={`flex items-center justify-center ${className}`}
                style={{ backgroundColor: isDark ? '#141414' : '#f5f5f5' }}
            >
                <div className="text-center p-8">
                    <Box className="w-16 h-16 mx-auto mb-4" style={{ color: `${primaryColor}40` }} />
                    <p style={{ color: isDark ? '#666' : '#888' }}>No 3D model available</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`} style={{ backgroundColor }}>
            {/* Loading Overlay */}
            {isLoading && (
                <div 
                    className="absolute inset-0 flex flex-col items-center justify-center z-20"
                    style={{ backgroundColor: isDark ? '#0D0D0D' : '#f5f5f5' }}
                >
                    <div className="relative mb-4">
                        <div 
                            className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin"
                            style={{ borderColor: `${primaryColor}30`, borderTopColor: primaryColor }}
                        />
                        <Box 
                            className="w-6 h-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                            style={{ color: primaryColor }}
                        />
                    </div>
                    <p className="text-sm font-medium" style={{ color: primaryColor }}>
                        Loading 3D Model...
                    </p>
                    <p className="text-xs mt-1" style={{ color: isDark ? '#666' : '#888' }}>
                        {loadProgress}%
                    </p>
                    {/* Progress bar */}
                    <div 
                        className="w-32 h-1 rounded-full mt-3 overflow-hidden"
                        style={{ backgroundColor: isDark ? '#333' : '#ddd' }}
                    >
                        <div 
                            className="h-full rounded-full transition-all duration-300"
                            style={{ 
                                width: `${loadProgress}%`,
                                backgroundColor: primaryColor 
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div 
                    className="absolute inset-0 flex flex-col items-center justify-center z-20"
                    style={{ backgroundColor: isDark ? '#0D0D0D' : '#f5f5f5' }}
                >
                    <Box className="w-16 h-16 mb-4 text-red-500" />
                    <p className="text-red-500 font-medium">{error}</p>
                    <p className="text-sm mt-1" style={{ color: isDark ? '#666' : '#888' }}>
                        Please try refreshing the page
                    </p>
                </div>
            )}

            {/* Model Viewer */}
            <model-viewer
                ref={modelViewerRef}
                src={src}
                alt={alt}
                poster={poster}
                ar
                ar-modes="webxr scene-viewer quick-look"
                ar-scale="auto"
                camera-controls
                touch-action="pan-y"
                auto-rotate
                auto-rotate-delay="0"
                rotation-per-second="15deg"
                shadow-intensity="1"
                shadow-softness="1"
                exposure="1.2"
                environment-image="neutral"
                interaction-prompt="auto"
                interaction-prompt-style="basic"
                loading="eager"
                reveal="auto"
                style={{ 
                    width: '100%', 
                    height: '100%',
                    backgroundColor: 'transparent',
                    '--poster-color': 'transparent'
                }}
                data-testid="model-viewer-3d"
            >
                {/* Hide default AR button - we'll use our custom one */}
                <button slot="ar-button" style={{ display: 'none' }} />
                
                {/* Custom loading indicator inside model-viewer */}
                <div slot="progress-bar" className="hidden" />
            </model-viewer>

            {/* 3D Badge */}
            <div 
                className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 backdrop-blur-md z-10"
                style={{ backgroundColor: `${primaryColor}CC`, color: '#fff' }}
            >
                <Box className="w-3.5 h-3.5" /> 3D Model
            </div>

            {/* Control Buttons */}
            {!isLoading && !error && (
                <div className="absolute bottom-4 left-4 flex gap-2 z-10">
                    <button
                        onClick={handleResetCamera}
                        className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all hover:scale-105"
                        style={{ 
                            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                            color: isDark ? '#fff' : '#333'
                        }}
                        title="Reset view"
                        data-testid="reset-camera-btn"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={handleZoomIn}
                        className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all hover:scale-105"
                        style={{ 
                            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                            color: isDark ? '#fff' : '#333'
                        }}
                        title="Zoom in"
                        data-testid="zoom-in-btn"
                    >
                        <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all hover:scale-105"
                        style={{ 
                            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                            color: isDark ? '#fff' : '#333'
                        }}
                        title="Zoom out"
                        data-testid="zoom-out-btn"
                    >
                        <ZoomOut className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* AR Button - Only shown on supported devices */}
            {!isLoading && !error && arSupported && (
                <div className="absolute bottom-4 right-4 z-10">
                    <Button
                        onClick={handleViewAR}
                        className="px-6 py-5 text-sm font-medium rounded-full shadow-xl transition-all hover:scale-105"
                        style={{ 
                            backgroundColor: primaryColor,
                            color: '#fff',
                            boxShadow: `0 8px 32px ${primaryColor}50`
                        }}
                        data-testid="ar-button"
                    >
                        <View className="w-4 h-4 mr-2" />
                        View in AR
                    </Button>
                </div>
            )}

            {/* AR Not Supported Message - Only shown on desktop */}
            {!isLoading && !error && !arSupported && (
                <div 
                    className="absolute bottom-4 right-4 max-w-[200px] p-3 rounded-xl backdrop-blur-md z-10"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: isDark ? '#888' : '#666'
                    }}
                >
                    <div className="flex items-start gap-2">
                        <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                        <p className="text-xs leading-relaxed">
                            Open on your phone to view this dish in AR
                        </p>
                    </div>
                </div>
            )}

            {/* Interaction Hint */}
            {!isLoading && !error && (
                <div 
                    className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs backdrop-blur-md z-10"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        color: isDark ? '#888' : '#666'
                    }}
                >
                    Drag to rotate • Pinch to zoom
                </div>
            )}
        </div>
    );
}
